import React, { useState, useEffect } from 'react';
import {
  ConfirmCloseDialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/ConfirmCloseDialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { Sparkles, Loader2, Search, Building2 } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { useUnsavedChanges } from '@/hooks/useUnsavedChanges';
import { CompanyLookupInput, KVKCompany } from './CompanyLookupInput';

interface Organization {
  id: string;
  name: string;
  classification: string;
  status: string;
  sector?: string;
  kvk?: string;
  btw?: string;
  website?: string;
  phone?: string;
  email?: string;
  addresses: any[];
}

interface OrganizationModalProps {
  isOpen: boolean;
  onClose: () => void;
  organization?: Organization | null;
  onSave: () => void;
}

export const OrganizationModal: React.FC<OrganizationModalProps> = ({
  isOpen,
  onClose,
  organization,
  onSave,
}) => {
  const [formData, setFormData] = useState({
    name: '',
    classification: 'klant',
    status: 'prospect',
    sector: '',
    kvk: '',
    btw: '',
    website: '',
    phone: '',
    email: '',
    // Address fields
    street: '',
    house_number: '',
    postal_code: '',
    city: '',
    country: 'Nederland',
    visible_for_tenants: [] as string[],
  });
  const [isLoading, setIsLoading] = useState(false);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [enriching, setEnriching] = useState(false);
  const [faviconPreview, setFaviconPreview] = useState<string | null>(null);
  const [tenants, setTenants] = useState<Array<{ id: string; name: string }>>([]);
  const [useKvkLookup, setUseKvkLookup] = useState(!organization); // Default to KVK lookup for new orgs
  const { toast } = useToast();
  const { user, userRole } = useAuth();

  const { hasUnsavedChanges, resetInitialValues } = useUnsavedChanges(formData, isOpen);

  useEffect(() => {
    if (!isOpen) return;
    
    if (organization) {
      // Extract primary address from addresses array
      const primaryAddress = organization.addresses?.[0] || {};
      setFormData({
        name: organization.name || '',
        classification: organization.classification || 'klant',
        status: organization.status || 'prospect',
        sector: organization.sector || '',
        kvk: organization.kvk || '',
        btw: organization.btw || '',
        website: organization.website || '',
        phone: organization.phone || '',
        email: organization.email || '',
        street: primaryAddress.street || '',
        house_number: primaryAddress.house_number || '',
        postal_code: primaryAddress.postal_code || '',
        city: primaryAddress.city || '',
        country: primaryAddress.country || 'Nederland',
        visible_for_tenants: (organization as any).visible_for_tenants || [],
      });
      setLogoPreview((organization as any).logo_url || null);
      setFaviconPreview((organization as any).favicon_url || null);
    } else {
      setFormData({
        name: '',
        classification: 'klant',
        status: 'prospect',
        sector: '',
        kvk: '',
        btw: '',
        website: '',
        phone: '',
        email: '',
        street: '',
        house_number: '',
        postal_code: '',
        city: '',
        country: 'Nederland',
        visible_for_tenants: [],
      });
      setLogoPreview(null);
      setFaviconPreview(null);
    }
    setLogoFile(null);
    
    // Reset initial values after form data is populated (next tick)
    const timeout = setTimeout(() => {
      resetInitialValues();
    }, 0);
    return () => clearTimeout(timeout);
  }, [organization, isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch tenants for super_admin
  useEffect(() => {
    const fetchTenants = async () => {
      if (userRole === 'super_admin' && isOpen) {
        const { data, error } = await supabase
          .from('tenants')
          .select('id, name')
          .order('name');
        
        if (!error && data) {
          setTenants(data);
        }
      }
    };
    
    fetchTenants();
  }, [isOpen, userRole]);

  // Update favicon preview when website changes
  useEffect(() => {
    if (formData.website) {
      const domain = extractDomain(formData.website);
      setFaviconPreview(`https://www.google.com/s2/favicons?domain=${domain}&sz=128`);
    } else {
      setFaviconPreview(null);
    }
  }, [formData.website]);

  const extractDomain = (url: string) => {
    try {
      const urlObj = new URL(url.startsWith('http') ? url : `https://${url}`);
      return urlObj.hostname.replace('www.', '');
    } catch {
      return url;
    }
  };

  const handleLogoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleEnrichData = async () => {
    if (!formData.website) {
      toast({
        title: "Website vereist",
        description: "Voer eerst een website URL in om data te verrijken",
        variant: "destructive",
      });
      return;
    }

    setEnriching(true);
    try {
      const { data, error } = await supabase.functions.invoke('enrich-company-data', {
        body: { website: formData.website },
      });

      if (error) throw error;

      // Auto-fill fields with enriched data
      setFormData((prev) => ({
        ...prev,
        ...(data.title && !prev.name ? { name: data.title } : {}),
        ...(data.description ? { sector: data.description.substring(0, 255) } : {}),
      }));

      toast({
        title: "✨ Data verrijkt",
        description: `${data.scraped ? 'Bedrijfsgegevens opgehaald' : 'Favicon opgeslagen'}`,
      });

    } catch (error: any) {
      console.error('Error enriching data:', error);
      toast({
        title: "Fout bij verrijken",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setEnriching(false);
    }
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    
    if (!user) {
      toast({
        title: 'Fout',
        description: 'Je moet ingelogd zijn om organisaties te beheren.',
        variant: 'destructive',
      });
      return;
    }

    if (!formData.name.trim()) {
      toast({
        title: 'Fout',
        description: 'Naam is verplicht.',
        variant: 'destructive',
      });
      return;
    }

    setIsLoading(true);

    try {
      // Format website URL if provided
      let formattedWebsite = formData.website.trim() || null;
      if (formattedWebsite && !formattedWebsite.startsWith('http://') && !formattedWebsite.startsWith('https://')) {
        formattedWebsite = `https://${formattedWebsite}`;
      }

      let logoUrl = organization ? (organization as any).logo_url : null;
      let orgId = organization?.id || crypto.randomUUID();

      // Upload logo if a new file was selected
      if (logoFile) {
        const fileExt = logoFile.name.split('.').pop();
        const fileName = `org-logos/${orgId}.${fileExt}`;
        
        const { error: uploadError } = await supabase.storage
          .from('pictures')
          .upload(fileName, logoFile, { upsert: true });
        
        if (uploadError) throw uploadError;
        
        const { data: { publicUrl } } = supabase.storage
          .from('pictures')
          .getPublicUrl(fileName);
        
        logoUrl = publicUrl;
      }

      const domain = formattedWebsite ? extractDomain(formattedWebsite) : null;
      const faviconUrl = domain ? `https://www.google.com/s2/favicons?domain=${domain}&sz=128` : null;

      // Build address object
      const addressData = {
        street: formData.street.trim() || null,
        house_number: formData.house_number.trim() || null,
        postal_code: formData.postal_code.trim() || null,
        city: formData.city.trim() || null,
        country: formData.country.trim() || null,
      };

      // Only include address if at least street or city is filled
      const hasAddress = addressData.street || addressData.city;

      const organizationData: any = {
        name: formData.name.trim(),
        classification: formData.classification,
        status: formData.status,
        sector: formData.sector.trim() || null,
        kvk: formData.kvk.trim() || null,
        btw: formData.btw.trim() || null,
        website: formattedWebsite,
        phone: formData.phone.trim() || null,
        email: formData.email.trim() || null,
        addresses: hasAddress ? [addressData] : [],
        favicon_url: faviconUrl,
        logo_url: logoUrl,
        relation_owner_id: user.id,
      };

      // Only super_admin can set visible_for_tenants - always send to allow clearing
      if (userRole === 'super_admin') {
        organizationData.visible_for_tenants = formData.visible_for_tenants;
      }

      if (organization) {
        // Update existing organization
        const { error } = await supabase
          .from('organizations')
          .update(organizationData)
          .eq('id', organization.id);

        if (error) throw error;

        toast({
          title: 'Organisatie bijgewerkt',
          description: 'De organisatie is succesvol bijgewerkt.',
        });
      } else {
        // Create new organization with the pre-generated UUID if logo was uploaded
        const insertData = logoFile ? { ...organizationData, id: orgId } : organizationData;
        const { error } = await supabase
          .from('organizations')
          .insert([insertData]);

        if (error) throw error;

        toast({
          title: 'Organisatie aangemaakt',
          description: 'De organisatie is succesvol aangemaakt.',
        });
      }

      resetInitialValues();
      onSave();
      onClose();
    } catch (error: any) {
      console.error('Error saving organization:', error);
      toast({
        title: 'Fout',
        description: 'Er is een fout opgetreden bij het opslaan van de organisatie.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <ConfirmCloseDialog
      open={isOpen}
      onOpenChange={(open) => !open && onClose()}
      hasUnsavedChanges={hasUnsavedChanges}
      onConfirmDiscard={onClose}
      onConfirmSave={handleSubmit}
      isSaving={isLoading}
    >
      <DialogContent className="sm:max-w-[425px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {organization ? 'Organisatie bewerken' : 'Nieuwe organisatie'}
          </DialogTitle>
          <DialogDescription>
            {organization 
              ? 'Bewerk de gegevens van de organisatie.' 
              : 'Vul de gegevens in voor de nieuwe organisatie.'
            }
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label htmlFor="name">Naam *</Label>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setUseKvkLookup(!useKvkLookup)}
                className="text-xs h-7"
              >
                {useKvkLookup ? (
                  <>
                    <Building2 className="h-3 w-3 mr-1" />
                    Handmatig invoeren
                  </>
                ) : (
                  <>
                    <Search className="h-3 w-3 mr-1" />
                    Zoek op KVK
                  </>
                )}
              </Button>
            </div>
            {useKvkLookup ? (
              <CompanyLookupInput
                onSelect={(company: KVKCompany) => {
                  setFormData(prev => ({
                    ...prev,
                    name: company.name,
                    kvk: company.kvkNumber,
                    street: company.street || '',
                    house_number: company.houseNumber || '',
                    postal_code: company.postalCode || '',
                    city: company.city || '',
                  }));
                  // Switch to manual mode after selection to allow editing
                  setUseKvkLookup(false);
                }}
                placeholder="Zoek op bedrijfsnaam of KVK-nummer..."
                initialValue={formData.name}
              />
            ) : (
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Naam van de organisatie"
                required
              />
            )}
          </div>

          <div>
            <Label htmlFor="classification">Classificatie</Label>
            <Select
              value={formData.classification}
              onValueChange={(value) => setFormData({ ...formData, classification: value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecteer classificatie" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="klant">Klant</SelectItem>
                <SelectItem value="leverancier">Leverancier</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="status">Status</Label>
            <Select
              value={formData.status}
              onValueChange={(value) => setFormData({ ...formData, status: value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecteer status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="prospect">Prospect</SelectItem>
                <SelectItem value="lead">Lead</SelectItem>
                <SelectItem value="klant">Klant</SelectItem>
                <SelectItem value="actief">Actief</SelectItem>
                <SelectItem value="inactief">Inactief</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="sector">Sector</Label>
            <Input
              id="sector"
              value={formData.sector}
              onChange={(e) => setFormData({ ...formData, sector: e.target.value })}
              placeholder="Bijv. Industrie, Retail, etc."
            />
          </div>

          <div>
            <Label htmlFor="kvk">KvK-nummer</Label>
            <Input
              id="kvk"
              value={formData.kvk}
              onChange={(e) => setFormData({ ...formData, kvk: e.target.value })}
              placeholder="12345678"
            />
          </div>

          <div>
            <Label htmlFor="btw">BTW-nummer</Label>
            <Input
              id="btw"
              value={formData.btw}
              onChange={(e) => setFormData({ ...formData, btw: e.target.value })}
              placeholder="NL123456789B01"
            />
          </div>

          <div>
            <Label htmlFor="website">Website</Label>
            <div className="flex gap-2">
              <Input
                id="website"
                value={formData.website}
                onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                placeholder="www.example.com of https://www.example.com"
                className="flex-1"
              />
              <Button
                type="button"
                variant="outline"
                onClick={handleEnrichData}
                disabled={!formData.website || enriching}
                title="Data verrijken"
              >
                {enriching ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Sparkles className="h-4 w-4" />
                )}
              </Button>
            </div>
            {faviconPreview && (
              <div className="flex items-center gap-2 mt-2 p-2 bg-muted rounded-md">
                <img src={faviconPreview} alt="Favicon" className="w-4 h-4" />
                <span className="text-sm text-muted-foreground">Favicon preview</span>
              </div>
            )}
          </div>

          {/* Contact Information Section */}
          <div className="space-y-4 border-t pt-4 mt-4">
            <h4 className="font-medium text-sm text-muted-foreground">Contactgegevens</h4>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="phone">Telefoonnummer</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+31 20 123 4567"
                />
              </div>
              <div>
                <Label htmlFor="email">E-mailadres</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="info@bedrijf.nl"
                />
              </div>
            </div>
          </div>

          {/* Address Section */}
          <div className="space-y-4 border-t pt-4 mt-4">
            <h4 className="font-medium text-sm text-muted-foreground">Adresgegevens</h4>
            
            <div className="grid grid-cols-3 gap-4">
              <div className="col-span-2">
                <Label htmlFor="street">Straat</Label>
                <Input
                  id="street"
                  value={formData.street}
                  onChange={(e) => setFormData({ ...formData, street: e.target.value })}
                  placeholder="Keizersgracht"
                />
              </div>
              <div>
                <Label htmlFor="house_number">Huisnr.</Label>
                <Input
                  id="house_number"
                  value={formData.house_number}
                  onChange={(e) => setFormData({ ...formData, house_number: e.target.value })}
                  placeholder="100"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label htmlFor="postal_code">Postcode</Label>
                <Input
                  id="postal_code"
                  value={formData.postal_code}
                  onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                  placeholder="1015 CW"
                />
              </div>
              <div className="col-span-2">
                <Label htmlFor="city">Plaats</Label>
                <Input
                  id="city"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  placeholder="Amsterdam"
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="country">Land</Label>
              <Input
                id="country"
                value={formData.country}
                onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                placeholder="Nederland"
              />
            </div>
          </div>

          {userRole === 'super_admin' && tenants.length > 0 && (
            <div>
              <Label>Zichtbaar voor tenants</Label>
              <div className="space-y-2 mt-2 p-4 border rounded-md">
                {tenants.map((tenant) => (
                  <div key={tenant.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={`tenant-${tenant.id}`}
                      checked={formData.visible_for_tenants.includes(tenant.id)}
                      onCheckedChange={(checked) => {
                        setFormData((prev) => ({
                          ...prev,
                          visible_for_tenants: checked
                            ? [...prev.visible_for_tenants, tenant.id]
                            : prev.visible_for_tenants.filter((id) => id !== tenant.id),
                        }));
                      }}
                    />
                    <Label htmlFor={`tenant-${tenant.id}`} className="cursor-pointer font-normal">
                      {tenant.name}
                    </Label>
                  </div>
                ))}
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Selecteer voor welke tenants deze organisatie zichtbaar moet zijn
              </p>
            </div>
          )}

          <div>
            <Label htmlFor="logo">Logo</Label>
            <div className="space-y-2">
              {logoPreview && (
                <div className="w-32 h-32 bg-muted rounded-lg flex items-center justify-center overflow-hidden mx-auto">
                  <img 
                    src={logoPreview} 
                    alt="Logo preview"
                    className="w-full h-full object-contain p-2"
                  />
                </div>
              )}
              <Input
                id="logo"
                type="file"
                accept="image/*"
                onChange={handleLogoChange}
                className="cursor-pointer"
              />
              <p className="text-xs text-muted-foreground">
                Upload een logo voor deze organisatie (optioneel)
              </p>
            </div>
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Annuleren
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? 'Bezig...' : organization ? 'Bijwerken' : 'Opslaan'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </ConfirmCloseDialog>
  );
};
